import React, { KeyboardEvent } from 'react';

import Button from '../../../../components/buttons/Button';
import { browserHistory } from '../../../../browserHistory';

import '../Widget.scss';
import './GoToWidget.scss';
import Swal from 'sweetalert2';
import analyticsWrapper from '../../../../utils/analyticsWrapper';

interface Props {
  floatingWidget?: boolean;
}

interface State {
  orderId: string;
  bundleId: string;
  orderSearch: string;
  deliveryId: string;
  externalId: string;
}

function removeSpaceBetweenNumbersInString(query: string): string {
  const regex = /(\d)\s+(?=\d)/g;
  const str = query;
  const subst = `$1`;

  // The substituted value will be contained in the result variable
  const result = str.replace(regex, subst);
  return result;
}

class GoToWidget extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      orderId: '',
      bundleId: '',
      orderSearch: '',
      deliveryId: '',
      externalId: '',
    };
  }

  goToOrderSearch = (query: string): void => {
    const { floatingWidget } = this.props;
    analyticsWrapper.event({
      category: 'navigation',
      action: `${
        floatingWidget ? 'floating-widget-' : ''
      }goto-widget-order-search`,
    });

    this.setState(
      {
        orderId: '',
        bundleId: '',
        orderSearch: '',
        deliveryId: '',
        externalId: '',
      },
      () =>
        browserHistory.push(
          `/orders/search?q=${removeSpaceBetweenNumbersInString(query).trim()}`
        )
    );
  };

  goToOrder = (orderId: string): void => {
    const { floatingWidget } = this.props;
    if (isNaN(parseInt(orderId.trim()))) {
      Swal.fire(
        'Not a valid Order ID',
        'The Order ID must be a number',
        'error'
      );
      return;
    }
    analyticsWrapper.event({
      category: 'navigation',
      action: `${floatingWidget ? 'floating-widget-' : ''}goto-widget-order`,
    });
    this.setState(
      {
        orderId: '',
        bundleId: '',
        orderSearch: '',
        deliveryId: '',
        externalId: '',
      },
      () => browserHistory.push(`/orders/${orderId.trim()}`)
    );
  };

  goToBundle = (bundleId: string): void => {
    const { floatingWidget } = this.props;
    if (isNaN(parseInt(bundleId.trim()))) {
      Swal.fire(
        'Not a valid Bundle ID',
        'The Bundle ID must be a number',
        'error'
      );
      return;
    }
    analyticsWrapper.event({
      category: 'navigation',
      action: `${floatingWidget ? 'floating-widget-' : ''}goto-widget-bundle`,
    });
    this.setState(
      {
        orderId: '',
        bundleId: '',
        orderSearch: '',
        deliveryId: '',
        externalId: '',
      },
      () => browserHistory.push(`/bundles/${bundleId.trim()}`)
    );
  };

  goToDelivery = (deliveryId: string): void => {
    const { floatingWidget } = this.props;
    if (isNaN(parseInt(deliveryId.trim()))) {
      Swal.fire(
        'Not a valid Delivery ID',
        'The Delivery ID must be a number',
        'error'
      );
      return;
    }
    analyticsWrapper.event({
      category: 'navigation',
      action: `${floatingWidget ? 'floating-widget-' : ''}goto-widget-delivery`,
    });
    this.setState(
      {
        orderId: '',
        bundleId: '',
        orderSearch: '',
        deliveryId: '',
        externalId: '',
      },
      () => browserHistory.push(`/deliveries/${deliveryId.trim()}`)
    );
  };

  goToExternalIdSearch = (externalId: string): void => {
    const { floatingWidget } = this.props;
    analyticsWrapper.event({
      category: 'navigation',
      action: `${
        floatingWidget ? 'floating-widget-' : ''
      }goto-widget-order-search`,
    });

    this.setState(
      {
        orderId: '',
        bundleId: '',
        orderSearch: '',
        deliveryId: '',
        externalId: '',
      },
      () =>
        browserHistory.push(
          `/orders/search?q=${removeSpaceBetweenNumbersInString(
            externalId
          ).trim()}&mode=externalId`
        )
    );
  };

  handleOrderIdUpdate = (event: any): void => {
    this.setState({
      orderId: event.target.value,
    });
  };

  handleBundleIdUpdate = (event: any): void => {
    this.setState({
      bundleId: event.target.value,
    });
  };

  handleOrderSearchUpdate = (event: any): void => {
    this.setState({
      orderSearch: event.target.value,
    });
  };

  handleDeliveryIdUpdate = (event: any): void => {
    this.setState({
      deliveryId: event.target.value,
    });
  };

  handleExternalIdUpdate = (event: any): void => {
    this.setState({
      externalId: event.target.value,
    });
  };

  render(): JSX.Element {
    return (
      <div className="widget">
        <div className="widget__header">
          <h5>Directly to order/bundle</h5>
        </div>
        <div className="widget__text" style={{ fontSize: '0.8em' }}>
          Type in the order/bundle id below to go directly to it.
        </div>
        <div style={{ height: 10 }} />
        <div>
          <div>Order ID:</div>
          <div className="widget_goto-line">
            <input
              name={'orderId'}
              type={'text'}
              value={this.state.orderId}
              onChange={this.handleOrderIdUpdate}
              onKeyDown={this.checkForEnter}
              style={{ width: '10em', minWidth: '4em' }}
              className={'form-control'}
              data-lpignore="true"
            />
            <Button
              title={'Go'}
              onClick={() => this.goToOrder(this.state.orderId)}
              className="widget_goto-button"
            />
          </div>
        </div>
        <div>
          <div>Order search:</div>
          <div className="widget_goto-line">
            <input
              name={'orderSearch'}
              type={'text'}
              value={this.state.orderSearch}
              onChange={this.handleOrderSearchUpdate}
              onKeyDown={this.checkForEnter}
              style={{ width: '10em', minWidth: '4em' }}
              className={'form-control'}
              data-lpignore="true"
            />
            <Button
              title={'Go'}
              onClick={() => this.goToOrderSearch(this.state.orderSearch)}
              className="widget_goto-button"
            />
          </div>
        </div>
        <div>
          <div>Order Reference / Shipment Id</div>
          <div className="widget_goto-line">
            <input
              name={'externalId'}
              type={'text'}
              value={this.state.externalId}
              onChange={this.handleExternalIdUpdate}
              onKeyDown={this.checkForEnter}
              style={{ width: '10em', minWidth: '4em' }}
              className={'form-control'}
              data-lpignore="true"
            />
            <Button
              title={'Go'}
              onClick={() => this.goToExternalIdSearch(this.state.externalId)}
              className="widget_goto-button"
            />
          </div>
        </div>
        <div>
          <div>Bundle ID:</div>
          <div className="widget_goto-line">
            <input
              name={'bundleId'}
              type={'text'}
              value={this.state.bundleId}
              onChange={this.handleBundleIdUpdate}
              onKeyDown={this.checkForEnter}
              style={{ width: '10em', minWidth: '4em' }}
              className={'form-control'}
              data-lpignore="true"
            />
            <Button
              title={'Go'}
              onClick={() => this.goToBundle(this.state.bundleId)}
              className="widget_goto-button"
            />
          </div>
        </div>

        <div>
          <div>Delivery ID:</div>
          <div className="widget_goto-line">
            <input
              name={'deliveryId'}
              type={'text'}
              value={this.state.deliveryId}
              onChange={this.handleDeliveryIdUpdate}
              onKeyDown={this.checkForEnter}
              style={{ width: '10em', minWidth: '4em' }}
              className={'form-control'}
              data-lpignore="true"
            />
            <Button
              title={'Go'}
              onClick={() => this.goToDelivery(this.state.deliveryId)}
              className="widget_goto-button"
            />
          </div>
        </div>
      </div>
    );
  }

  checkForEnter = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      switch ((event.target as HTMLInputElement).name) {
        case 'orderId':
          this.goToOrder(this.state.orderId);
          break;
        case 'bundleId':
          this.goToBundle(this.state.bundleId);
          break;
        case 'orderSearch':
          this.goToOrderSearch(this.state.orderSearch);
          break;
        case 'deliveryId':
          this.goToDelivery(this.state.deliveryId);
          break;
      }
    }
  };
}

export default GoToWidget;
